import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  InputField,
  Textarea,
} from "@jem-open/jem-ui";
import { UserX } from "lucide-react";
import { AttendanceException } from "../services/model/AttendanceException";
import {
  getExceptionDetailsSummary,
  getExceptionShiftLabel,
  getShiftTimeRange,
} from "../utils/exceptionDisplayUtils";

interface NoShowActionModalProps {
  exception: AttendanceException;
  open: boolean;
  onClose: () => void;
  onResolve: (id: string, action: string, reason?: string) => void;
}

const NoShowActionModal: React.FC<NoShowActionModalProps> = ({
  exception,
  open,
  onClose,
  onResolve,
}) => {
  const [action, setAction] = useState<string>("");
  const [clockTime, setClockTime] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");

  useEffect(() => {
    if (open) {
      setAction("");
      setClockTime("");
      setOtherReason("");
    }
  }, [exception.id, open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-xs">
            <UserX size={18} className="text-red-500" />
            No Show — Resolve Exception
          </DialogTitle>
          <DialogDescription>
            {exception.employee_name} did not clock in for their shift at{" "}
            {exception.location_name}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-md py-sm">
          <div className="rounded-lg border border-greyscale-border p-sm space-y-xxs">
            <p className="text-sm">
              <span className="font-semibold">Employee:</span>{" "}
              {exception.employee_name}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Site:</span>{" "}
              {exception.location_name} — {exception.region_name}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Shift:</span>{" "}
              {getExceptionShiftLabel(exception)} (
              {getShiftTimeRange(exception.shift_start, exception.shift_end)})
            </p>
            <p className="text-sm text-greyscale-text-caption">
              {getExceptionDetailsSummary(exception)}
            </p>
          </div>

          <div className="space-y-xs">
            <Label className="text-sm font-semibold">Choose action</Label>
            <RadioGroup value={action} onValueChange={setAction}>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="replacement" id="no-show-replacement" />
                <Label htmlFor="no-show-replacement" className="text-sm">
                  Find Replacement
                </Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="sick" id="no-show-sick" />
                <Label htmlFor="no-show-sick" className="text-sm">
                  Mark as Sick
                </Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="family" id="no-show-family" />
                <Label htmlFor="no-show-family" className="text-sm">
                  Mark as Family Responsibility
                </Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="clock_in" id="no-show-clock-in" />
                <Label htmlFor="no-show-clock-in" className="text-sm">
                  Clock In — employee clocked in manually
                </Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="mark_as_other" id="no-show-other" />
                <Label htmlFor="no-show-other" className="text-sm">
                  Mark as Other — provide a reason
                </Label>
              </div>
            </RadioGroup>
          </div>

          {action === "clock_in" && (
            <div className="space-y-xxs">
              <Label
                htmlFor="no-show-clock-time"
                className="text-sm font-semibold"
              >
                Clock-in time
              </Label>
              <InputField
                id="no-show-clock-time"
                type="time"
                value={clockTime}
                onChange={(e) => setClockTime(e.target.value)}
              />
              <p className="text-xs text-greyscale-text-caption">
                Enter the time the employee actually started — a clock-in will
                be created at this time.
              </p>
            </div>
          )}

          {action === "mark_as_other" && (
            <div className="space-y-xxs">
              <Label
                htmlFor="no-show-other-reason"
                className="text-sm font-semibold"
              >
                Reason
              </Label>
              <Textarea
                id="no-show-other-reason"
                placeholder="Provide a reason for resolving this exception..."
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            disabled={
              !action ||
              (action === "clock_in" && !clockTime.trim()) ||
              (action === "mark_as_other" && !otherReason.trim())
            }
            onClick={() => {
              let reason: string | undefined;
              if (action === "clock_in") reason = clockTime;
              else if (action === "mark_as_other") reason = otherReason;
              onResolve(exception.id, action, reason);
              onClose();
            }}
          >
            {action === "replacement" ? "Find Replacement" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NoShowActionModal;
